import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Default query timeout — kept for reference but NOT injected into the Supabase client
// Injecting a custom fetch wrapper breaks Supabase auth's internal retry logic
export const SUPABASE_TIMEOUT_MS = 20000;

/**
 * Fetch wrapper that races the real request against a timeout AbortSignal.
 * Always provides an abort reason so downstream code can distinguish
 * timeout aborts from other cancellations.
 */
function fetchWithTimeout(url, options = {}, timeoutMs = SUPABASE_TIMEOUT_MS) {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutController?.abort(new DOMException('Request timed out', 'TimeoutError')),
    timeoutMs
  );

  const callerSignal = options?.signal;
  if (callerSignal) {
    if (callerSignal?.aborted) {
      clearTimeout(timeoutId);
      timeoutController?.abort(callerSignal?.reason);
    } else {
      callerSignal?.addEventListener(
        'abort',
        () => timeoutController?.abort(callerSignal?.reason),
        { once: true }
      );
    }
  }

  return fetch(url, { ...options, signal: timeoutController?.signal })?.finally(() =>
    clearTimeout(timeoutId)
  );
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
    },
    realtime: {
      params: {
        // Reduced from 10 → 5 to lower server-side broadcast pressure at 5000 users.
        // Each user receives at most 5 realtime events/second — sufficient for
        // field service workflows while cutting aggregate server load in half.
        eventsPerSecond: 5,
      },
    },
    // Global fetch options — applied to all REST/PostgREST requests
    global: {
      headers: {
        // Identify client version for server-side monitoring
        'x-client-info': 'servicejockey-web/1.0',
      },
    },
  }
);
